import "@src/packages/content/modules";
import { getDataPointForMarkerName } from "@src/packages/content/biomarkers";

/**
 * Normalize a marker label for the fallback identity. Diacritics and display
 * punctuation are presentation details, so they should not split a result's
 * history into separate series.
 */
export function normalizeBiomarkerName(
  markerName: string | null | undefined,
): string {
  return (markerName ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    // Keep letters and numbers from every script. Unknown labels should not
    // collapse to the same empty identity just because they are non-Latin.
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Return the stable identity for a biomarker label.
 *
 * Content data points carry the app's canonical aliases (for example AMH,
 * Anti-Mullerian Hormone, and the localized insight label all resolve to the
 * same data point). Unknown markers still get a deterministic normalized-name
 * identity without guessing that two unrelated tests are equivalent.
 */
export function getBiomarkerIdentity(
  markerName: string | null | undefined,
): string {
  const normalized = normalizeBiomarkerName(markerName);
  if (!normalized) return "name:";

  const dataPoint = getDataPointForMarkerName(markerName ?? "");
  if (dataPoint) return `content:${dataPoint.id}`;

  return `name:${normalized}`;
}

export function biomarkerNamesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  return getBiomarkerIdentity(left) === getBiomarkerIdentity(right);
}
